import { useState } from "react";
import { useTheme } from "../contexts/ThemeContext";
import { useAuth } from "../contexts/AuthContext";
import ThemeToggle from "../components/ui/ThemeToggle";

// Tela de cadastro de novo guia (tenant). Espelha visualmente a LoginPage
// e usa o mesmo mecanismo de persistência de token via AuthContext.register
// (que internamente bate em /Auth/register e grava no localStorage usando
// as MESMAS chaves do login).
//
// Após sucesso, o AuthContext seta `user`, o App.jsx re-renderiza pra
// Dashboard automaticamente (mesmo fluxo do login).
//
// Rate limit: 5 cadastros/hora/IP (policy auth-register no backend).

// Regras de senha — replicam o que o Identity exige hoje (Program.cs:41-46
// + default RequireLowercase=true do ASP.NET Core Identity). Mudou no
// backend? Atualiza aqui também.
const REGRAS_SENHA = [
  { label: "Pelo menos 6 caracteres", test: (s) => s.length >= 6 },
  { label: "Pelo menos 1 letra maiúscula", test: (s) => /[A-Z]/.test(s) },
  { label: "Pelo menos 1 letra minúscula", test: (s) => /[a-z]/.test(s) },
  { label: "Pelo menos 1 número", test: (s) => /[0-9]/.test(s) },
  { label: "Pelo menos 1 caractere especial", test: (s) => /[^a-zA-Z0-9]/.test(s) },
];

export default function RegisterPage({ onGoToLogin }) {
  const { C, isDark } = useTheme();
  const { register, loading, error } = useAuth();

  const [form, setForm] = useState({
    nome: "",
    email: "",
    telefone: "",
    documento: "",
    // Moeda do guia (Fase 2 — moeda-por-guia). Default EUR (mercado europeu inicial);
    // dropdown permite trocar pra BRL/USD. Definitiva no cadastro — não pode mudar depois.
    moeda: "EUR",
    senha: "",
    confirmarSenha: "",
  });
  const [clientError, setClientError] = useState(null);

  function update(key, value) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  // Validação client-side: rejeita antes de bater no servidor. Backend
  // valida tudo de novo (defesa em camadas), mas UX preventiva.
  function validar() {
    if (!form.nome.trim()) return "Informe seu nome.";
    if (!form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      return "E-mail inválido.";
    if (!form.telefone.trim()) return "Informe um telefone de contato.";
    if (!form.documento.trim()) return "Informe seu documento (CPF/IVA).";
    for (const regra of REGRAS_SENHA) {
      if (!regra.test(form.senha)) return `Senha: ${regra.label.toLowerCase()}.`;
    }
    if (form.senha !== form.confirmarSenha) return "As senhas não conferem.";
    return null;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    const erro = validar();
    if (erro) {
      setClientError(erro);
      return;
    }
    setClientError(null);
    await register({
      nome: form.nome.trim(),
      email: form.email.trim(),
      telefone: form.telefone.trim(),
      documento: form.documento.trim(),
      moeda: form.moeda,
      senha: form.senha,
    });
    // Sucesso → AuthContext seta `user` → App.jsx re-renderiza pra Dashboard.
    // Erro → AuthContext já populou `error` (vem do useAuth).
  }

  // Erro mostrado = client error (validação local) OU server error (do AuthContext)
  const erroExibido = clientError || error;
  const senhaPreenchida = form.senha.length > 0;

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: C.bg,
        fontFamily: "'DM Sans', sans-serif",
        transition: "background 0.3s ease",
        padding: "20px 0",
      }}
    >
      <form
        onSubmit={handleSubmit}
        style={{
          width: 420,
          maxWidth: "calc(100vw - 32px)",
          padding: 40,
          background: C.bgCard,
          borderRadius: 20,
          border: `1px solid ${C.border}`,
          boxShadow: isDark
            ? "0 20px 60px rgba(0,0,0,0.4)"
            : "0 20px 60px rgba(0,0,0,0.08)",
        }}
      >
        <div style={{ textAlign: "center", marginBottom: 28 }}>
          <img
            src="/tecmovel-logo.png"
            alt="Tecmovel"
            style={{ width: 64, height: 64, objectFit: "contain", marginBottom: 10 }}
          />
          <h1 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: C.text }}>
            Criar conta
          </h1>
          <p style={{ fontSize: 12, color: C.textMuted, marginTop: 6 }}>
            Comece a usar o Tecmovel Turismo
          </p>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <Campo label="Nome">
            <input
              value={form.nome}
              onChange={(e) => update("nome", e.target.value)}
              placeholder="Nome completo"
              required
              autoComplete="name"
              style={inputStyle(C)}
            />
          </Campo>

          <Campo label="E-mail">
            <input
              value={form.email}
              onChange={(e) => update("email", e.target.value)}
              type="email"
              placeholder="seu@email.com"
              required
              autoComplete="email"
              style={inputStyle(C)}
            />
          </Campo>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <Campo label="Telefone">
              <input
                value={form.telefone}
                onChange={(e) => update("telefone", e.target.value)}
                placeholder="+55 11..."
                required
                autoComplete="tel"
                style={inputStyle(C)}
              />
            </Campo>
            <Campo label="Documento" hint="CPF / IVA">
              <input
                value={form.documento}
                onChange={(e) => update("documento", e.target.value)}
                placeholder="000.000.000-00"
                required
                style={inputStyle(C)}
              />
            </Campo>
          </div>

          <Campo label="Moeda" hint="moeda dos seus lançamentos">
            <select
              value={form.moeda}
              onChange={(e) => update("moeda", e.target.value)}
              required
              style={inputStyle(C)}
            >
              <option value="EUR">EUR — Euro</option>
              <option value="BRL">BRL — Real</option>
              <option value="USD">USD — Dólar</option>
            </select>
          </Campo>

          <Campo label="Senha">
            <input
              value={form.senha}
              onChange={(e) => update("senha", e.target.value)}
              type="password"
              placeholder="••••••••"
              required
              autoComplete="new-password"
              style={inputStyle(C)}
            />
            {/* Checklist dinâmico — só aparece quando começou a digitar.
                Cada regra fica verde quando satisfeita. */}
            {senhaPreenchida && (
              <div
                style={{
                  marginTop: 8,
                  padding: "8px 12px",
                  background: C.bg,
                  border: `1px solid ${C.border}`,
                  borderRadius: 8,
                  display: "flex",
                  flexDirection: "column",
                  gap: 3,
                }}
              >
                {REGRAS_SENHA.map((regra) => {
                  const ok = regra.test(form.senha);
                  return (
                    <div
                      key={regra.label}
                      style={{
                        fontSize: 11,
                        color: ok ? C.green : C.textMuted,
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                      }}
                    >
                      <span style={{ width: 14, display: "inline-block" }}>
                        {ok ? "✓" : "○"}
                      </span>
                      {regra.label}
                    </div>
                  );
                })}
              </div>
            )}
          </Campo>

          <Campo label="Confirmar senha">
            <input
              value={form.confirmarSenha}
              onChange={(e) => update("confirmarSenha", e.target.value)}
              type="password"
              placeholder="••••••••"
              required
              autoComplete="new-password"
              style={inputStyle(C)}
            />
          </Campo>

          {erroExibido && (
            <div
              style={{
                background: C.redDim,
                color: C.red,
                padding: "10px 14px",
                borderRadius: 8,
                fontSize: 12,
                fontWeight: 500,
                textAlign: "center",
              }}
            >
              {erroExibido}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            style={{
              width: "100%",
              padding: "13px",
              borderRadius: 10,
              border: "none",
              background: `linear-gradient(135deg, ${C.accent}, ${C.accentLight})`,
              color: isDark ? "#0B1219" : "#fff",
              fontSize: 14,
              fontWeight: 700,
              cursor: loading ? "wait" : "pointer",
              fontFamily: "'DM Sans', sans-serif",
              marginTop: 4,
              opacity: loading ? 0.7 : 1,
            }}
          >
            {loading ? "Criando conta..." : "Criar conta"}
          </button>

          <button
            type="button"
            onClick={onGoToLogin}
            style={{
              background: "none",
              border: "none",
              color: C.textMuted,
              fontSize: 12,
              cursor: "pointer",
              fontFamily: "'DM Sans', sans-serif",
              padding: "8px 0",
              textAlign: "center",
            }}
          >
            Já tem conta? <span style={{ color: C.accent, fontWeight: 600 }}>Entrar</span>
          </button>
        </div>

        <div style={{ display: "flex", justifyContent: "center", marginTop: 14 }}>
          <ThemeToggle collapsed={false} />
        </div>

        <p style={{ textAlign: "center", fontSize: 11, color: C.textDim, marginTop: 12 }}>
          Tecmovel Sistemas · v1.0.0
        </p>
      </form>
    </div>
  );
}

function Campo({ label, hint, children }) {
  const { C } = useTheme();
  return (
    <div>
      <label
        style={{
          fontSize: 12,
          fontWeight: 600,
          color: C.textMuted,
          display: "block",
          marginBottom: 5,
        }}
      >
        {label}
        {hint && (
          <span style={{ color: C.textDim, fontWeight: 400, marginLeft: 6 }}>
            ({hint})
          </span>
        )}
      </label>
      {children}
    </div>
  );
}

function inputStyle(C) {
  return {
    width: "100%",
    padding: "11px 14px",
    borderRadius: 10,
    border: `1px solid ${C.border}`,
    background: C.inputBg,
    color: C.text,
    fontSize: 13,
    outline: "none",
    fontFamily: "'DM Sans', sans-serif",
    boxSizing: "border-box",
  };
}
